import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useLocation, useParams } from "react-router-dom";
import { Loader2, Download, CalendarClock, ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import DimensionRadar from "../components/DimensionRadar";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const BOOKING_URL = "https://outlook.office.com/bookwithme/user/24a4c744ca1c4170999f0b5d4a34f631@ariovis.fr?anonymous&ep=plink";

const SOLUTION_META = {
    nim: { name: "Netwrix Identity Manager", accent: "#9E0E40" },
    ping: { name: "Ping Identity", accent: "#DE0F72" },
    axiomatics: { name: "Axiomatics", accent: "#8A0E9E" },
    nps: { name: "Netwrix Privilege Secure", accent: "#FF9900" },
    keeper: { name: "Keeper", accent: "#FF76B7" },
    ariovis: { name: "Ariovis", accent: "#0f172a" },
};

function KpiCard({ label, value, caption, accent = false, testid }) {
    return (
        <div
            className={`rounded-md border p-6 ${accent ? "border-ariovis-bordeaux bg-white brand-shadow" : "border-slate-200 bg-white"}`}
            data-testid={testid}
        >
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</div>
            <div className={`mt-2 text-3xl font-semibold ${accent ? "text-ariovis-bordeaux" : "text-slate-950"}`}>{value}</div>
            {caption && <div className="mt-2 text-sm text-slate-600">{caption}</div>}
        </div>
    );
}

function SolutionCard({ sid, meta, capabilities, tagline, role, score }) {
    const s = SOLUTION_META[sid] || { name: sid, accent: "#9E0E40" };
    return (
        <article
            className="group rounded-md border border-slate-200 bg-white p-6 transition-colors hover:border-ariovis-fuschia"
            data-testid={`solution-card-${sid}`}
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{role}</div>
                    <h3 className="mt-1 text-xl font-semibold text-slate-950">{meta?.name || s.name}</h3>
                </div>
                {typeof score === "number" && (
                    <Badge
                        variant="outline"
                        className="rounded-sm border-ariovis-bordeaux/40 bg-[#fef3f8] text-ariovis-bordeaux"
                        data-testid={`sol-score-${sid}`}
                    >
                        score {score.toFixed(1)}
                    </Badge>
                )}
            </div>
            {tagline && (
                <p className="mt-3 border-l-2 border-ariovis-bordeaux pl-3 text-sm italic text-slate-700">
                    « {tagline} »
                </p>
            )}
            <ul className="mt-4 space-y-1 text-sm text-slate-600">
                {(capabilities || []).map((c, i) => (
                    <li key={i}>· {c}</li>
                ))}
            </ul>
        </article>
    );
}

export default function Results() {
    const { lang, t } = useI18n();
    const { lang: urlLang } = useParams();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const token = useMemo(() => new URLSearchParams(location.search).get("t"), [location.search]);

    useEffect(() => {
        if (!token) {
            setError(lang === "fr" ? "Aucun rapport à afficher. Reprenez le diagnostic." : "No report to display. Retake the diagnostic.");
            setLoading(false);
            return;
        }
        setLoading(true);
        Promise.all([
            axios.get(`${API}/report/${encodeURIComponent(token)}?lang=${lang}`),
            axios.get(`${API}/config?lang=${lang}`),
        ])
            .then(([r1, r2]) => {
                setData(r1.data);
                setConfig(r2.data);
            })
            .catch(() => {
                setError(lang === "fr" ? "Rapport introuvable ou expiré." : "Report not found or expired.");
            })
            .finally(() => setLoading(false));
    }, [token, lang]);

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center text-slate-500">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {lang === "fr" ? "Chargement…" : "Loading…"}
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="mx-auto max-w-2xl px-4 py-24 text-center">
                <h1 className="text-2xl font-semibold text-slate-950">{error}</h1>
                <Button asChild className="mt-6 rounded-sm bg-ariovis-bordeaux text-white hover:bg-ariovis-fuschia">
                    <Link to={`/${lang}/wizard`}>{t("nav.start")}</Link>
                </Button>
            </div>
        );
    }

    const result = data.result;
    const dims = (config?.dimensions || []).map((d) => ({
        dim: d.id,
        label: d[lang] || d.fr || d.id,
        score: result.scores[d.id] ?? 0,
    }));
    const solutionsById = Object.fromEntries((config?.solutions || []).map((s) => [s.id, s]));

    const pdfHref = `${API}/report/${encodeURIComponent(token)}/pdf?lang=${lang}`;

    return (
        <>
            <Seo
                title={
                    lang === "fr"
                        ? "Votre feuille de route PAM personnalisée | Ariovis"
                        : "Your personalized PAM roadmap | Ariovis"
                }
                description={
                    lang === "fr"
                        ? "Retrouvez votre diagnostic PAM détaillé, l'architecture cible et les prochaines étapes."
                        : "Read your detailed PAM diagnostic, target architecture and next steps."
                }
                path={`/${urlLang || lang}/results`}
                lang={lang}
                noindex
            />

            <section className="border-b border-slate-200 bg-white" data-testid="results-hero">
                <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.32em] text-ariovis-bordeaux">
                                {t("results.eyebrow")}
                            </div>
                            <h1 className="mt-3 max-w-3xl font-display text-3xl font-semibold text-slate-950 sm:text-4xl">
                                {result.profile_meta.name}
                            </h1>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                asChild
                                className="rounded-sm bg-ariovis-bordeaux text-white hover:bg-ariovis-fuschia"
                                data-testid="cta-pdf"
                            >
                                <a href={pdfHref} target="_blank" rel="noreferrer">
                                    <Download className="mr-2 h-4 w-4" />
                                    {t("results.cta_pdf")}
                                </a>
                            </Button>
                            <Button
                                asChild
                                variant="outline"
                                className="rounded-sm border-slate-950 text-slate-950 hover:bg-slate-950 hover:text-white"
                                data-testid="cta-expert"
                            >
                                <a href={BOOKING_URL} target="_blank" rel="noreferrer">
                                    <CalendarClock className="mr-2 h-4 w-4" />
                                    {t("results.cta_expert")}
                                </a>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-14">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid gap-4 md:grid-cols-3">
                        <KpiCard
                            label={t("results.current")}
                            value={`N${result.current_level} · ${result.current_level_meta.name}`}
                            caption={result.current_level_meta.message}
                            testid="kpi-current"
                        />
                        <KpiCard
                            label={t("results.target")}
                            value={`N${result.target_level} · ${result.target_level_meta.name}`}
                            caption={result.target_level_meta.message}
                            accent
                            testid="kpi-target"
                        />
                        <KpiCard
                            label={t("results.confidence")}
                            value={`${Math.round(result.confidence * 100)}%`}
                            caption={lang === "fr" ? "Basé sur la part de réponses fermes." : "Based on the share of firm answers."}
                            testid="kpi-confidence"
                        />
                    </div>

                    <div className="mt-8 rounded-md border border-slate-200 bg-white p-6 sm:p-8" data-testid="results-synthesis">
                        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-ariovis-bordeaux">
                            {t("results.synthesis")}
                        </div>
                        <p className="mt-3 text-lg leading-relaxed text-slate-800">{result.synthesis}</p>
                    </div>

                    <div className="mt-8 grid gap-4 lg:grid-cols-5">
                        <div className="rounded-md border border-slate-200 bg-white p-6 lg:col-span-3">
                            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-ariovis-bordeaux">
                                {t("results.radar")}
                            </div>
                            <div className="mt-3">
                                <DimensionRadar data={dims} height={360} />
                            </div>
                        </div>
                        <div className="rounded-md border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="results-top-risks">
                            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-ariovis-bordeaux">
                                {t("results.top_risks")}
                            </div>
                            {result.top_risks?.length ? (
                                <ol className="mt-4 space-y-2 text-sm text-slate-800">
                                    {result.top_risks.map((r, i) => (
                                        <li key={r} className="flex items-start gap-3">
                                            <span className="mt-0.5 shrink-0 rounded-sm bg-slate-950 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span>{r.replaceAll("_", " ")}</span>
                                        </li>
                                    ))}
                                </ol>
                            ) : (
                                <p className="mt-4 text-sm text-slate-600">
                                    {lang === "fr" ? "Aucun risque prioritaire signalé." : "No priority risk flagged."}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Solutions */}
                    <div className="mt-14" data-testid="results-solutions">
                        <div className="flex items-baseline justify-between">
                            <h2 className="font-display text-2xl font-semibold text-slate-950 sm:text-3xl">
                                {t("results.solutions")}
                            </h2>
                        </div>
                        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                            {result.recommended_solutions.map((sid) => {
                                const meta = solutionsById[sid];
                                return (
                                    <SolutionCard
                                        key={sid}
                                        sid={sid}
                                        meta={meta}
                                        role={meta?.role || ""}
                                        tagline={meta?.tagline || ""}
                                        capabilities={meta?.capabilities || []}
                                        score={result.solution_scores[sid]}
                                    />
                                );
                            })}
                        </div>
                        {result.non_priority_solutions?.length > 0 && (
                            <div className="mt-6 rounded-md border border-dashed border-slate-300 bg-white p-6" data-testid="results-non-priority">
                                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                                    {t("results.non_priority")}
                                </div>
                                <ul className="mt-3 flex flex-wrap gap-2">
                                    {result.non_priority_solutions.map((sid) => (
                                        <li
                                            key={sid}
                                            className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-600"
                                        >
                                            {solutionsById[sid]?.name || SOLUTION_META[sid]?.name || sid}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Roadmap */}
                    <div className="mt-14" data-testid="results-roadmap">
                        <h2 className="font-display text-2xl font-semibold text-slate-950 sm:text-3xl">
                            {t("results.roadmap")}
                        </h2>
                        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                            {result.roadmap.map((step, idx) => (
                                <div
                                    key={step.id}
                                    className="relative rounded-md border border-slate-200 bg-white p-6"
                                    data-testid={`roadmap-step-${step.id}`}
                                >
                                    <div className="text-xs font-semibold uppercase tracking-widest text-ariovis-fuschia">
                                        {t("results.step_prefix")} {String(idx + 1).padStart(2, "0")}
                                    </div>
                                    <h3 className="mt-1 text-lg font-semibold text-slate-950">{step.name}</h3>
                                    <p className="mt-2 text-sm text-slate-700">{step.objective}</p>
                                    {step.actions?.length > 0 && (
                                        <>
                                            <div className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                                                {t("results.actions")}
                                            </div>
                                            <ul className="mt-1 space-y-1 text-xs text-slate-700">
                                                {step.actions.map((a, i) => (
                                                    <li key={i}>· {a}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                    {step.solutions?.length > 0 && (
                                        <div className="mt-4 flex flex-wrap gap-1.5">
                                            {step.solutions.map((sid) => (
                                                <span
                                                    key={sid}
                                                    className="rounded-sm bg-slate-950 px-2 py-0.5 text-[10px] font-semibold text-white"
                                                >
                                                    {solutionsById[sid]?.name || SOLUTION_META[sid]?.name || sid}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Architecture */}
                    <div className="mt-14" data-testid="results-architecture">
                        <h2 className="font-display text-2xl font-semibold text-slate-950 sm:text-3xl">
                            {t("results.architecture")}
                        </h2>
                        <div className="mt-6 overflow-hidden rounded-md border border-slate-200 bg-white">
                            {result.architecture.map((layer) => {
                                const label = lang === "fr" ? layer.fr_label : layer.en_label;
                                const sol = layer.solution ? (solutionsById[layer.solution]?.name || SOLUTION_META[layer.solution]?.name) : null;
                                return (
                                    <div
                                        key={layer.id}
                                        className={`flex items-center justify-between border-b border-slate-100 px-6 py-4 last:border-b-0 ${
                                            layer.visible ? "bg-white" : "bg-slate-50 text-slate-400"
                                        }`}
                                    >
                                        <div className="text-sm font-semibold">{label}</div>
                                        {sol && layer.visible && (
                                            <div className="text-xs font-semibold uppercase tracking-widest text-ariovis-bordeaux">
                                                {sol}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <p className="mt-4 text-xs italic text-slate-500">{t("results.disclaimer")}</p>
                    </div>

                    {/* Quick wins */}
                    <div className="mt-14" data-testid="results-quick-wins">
                        <h2 className="font-display text-2xl font-semibold text-slate-950 sm:text-3xl">
                            {t("results.quick_wins")}
                        </h2>
                        <div className="mt-6 overflow-hidden rounded-md border border-slate-200 bg-white">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                                    <tr>
                                        <th className="px-4 py-3">{t("results.action_col")}</th>
                                        <th className="px-4 py-3">{t("results.impact")}</th>
                                        <th className="px-4 py-3">{t("results.effort")}</th>
                                        <th className="px-4 py-3">{t("results.solution_col")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.quick_wins.map((qw) => (
                                        <tr key={qw.id} className="border-t border-slate-100">
                                            <td className="px-4 py-3 font-medium text-slate-900">{qw.action}</td>
                                            <td className="px-4 py-3">
                                                <span className="rounded-sm bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                                                    {qw.impact}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">{qw.effort}</td>
                                            <td className="px-4 py-3 text-xs font-semibold uppercase tracking-widest text-ariovis-bordeaux">
                                                {solutionsById[qw.solution]?.name || SOLUTION_META[qw.solution]?.name || qw.solution}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Offers */}
                    <div className="mt-14" data-testid="results-offers">
                        <h2 className="font-display text-2xl font-semibold text-slate-950 sm:text-3xl">
                            {t("results.offers")}
                        </h2>
                        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                            {t("results.offer_names").map((n, i) => (
                                <div key={i} className="rounded-md border border-slate-200 bg-white p-6" data-testid={`offer-${i}`}>
                                    <div className="text-xs font-semibold uppercase tracking-widest text-ariovis-fuschia">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <h3 className="mt-1 text-lg font-semibold text-slate-950">{n}</h3>
                                    <p className="mt-2 text-sm text-slate-700">{t("results.offer_descs")[i]}</p>
                                    <a
                                        href={BOOKING_URL}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-ariovis-bordeaux hover:text-ariovis-fuschia"
                                    >
                                        {t("results.cta_expert")} <ArrowUpRight className="h-3.5 w-3.5" />
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
